import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Chip,
  Tooltip,
  useMediaQuery,
  Stack,
} from '@mui/material';
import { 
  ChevronLeft, 
  ChevronRight,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { getMonthsLongNames, getMonthIndex } from '../../utils/DateUtil';

interface MonthYearPickerProps {
  selectedMonth: string;
  selectedYear: number;
  onMonthChange: (month: string) => void;
  onYearChange: (year: number) => void;
  colorTheme?: 'error' | 'success' | 'primary' | 'secondary';
  title?: string;
  years?: number[];
  showFutureIndicator?: boolean;
  transactionCount?: number;
  isLoading?: boolean;
}

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  colorTheme = 'primary',
  transactionCount,
  isLoading = false,
}) => {
  const theme = useTheme();
  const monthNames = getMonthsLongNames();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Get theme colors based on colorTheme prop
  const themeColor = theme.palette[colorTheme];

  // Convert selectedMonth and selectedYear to a Dayjs object
  const selectedDate = dayjs()
    .year(selectedYear)
    .month(getMonthIndex(selectedMonth))
    .date(1);

  const today = dayjs();
  const currentMonthIndex = today.month();
  const currentYear = today.year();

  // Calculate period context
  const daysInMonth = selectedDate.daysInMonth();
  const isCurrentMonth = selectedYear === currentYear && getMonthIndex(selectedMonth) === currentMonthIndex;
  const dayOfMonth = isCurrentMonth ? today.date() : daysInMonth;
  const daysRemaining = isCurrentMonth ? daysInMonth - dayOfMonth : 0;

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate && newDate.isValid()) {
      const newYear = newDate.year();
      const newMonthIndex = newDate.month();
      const newMonth = monthNames[newMonthIndex];

      // Update both month and year
      if (newYear !== selectedYear) {
        onYearChange(newYear);
      }
      if (newMonth !== selectedMonth) {
        onMonthChange(newMonth);
      }
    }
  };

  // Navigation handlers
  const navigateToPreviousMonth = () => {
    const prevMonth = selectedDate.subtract(1, 'month');
    onYearChange(prevMonth.year());
    onMonthChange(monthNames[prevMonth.month()]);
  };

  const navigateToNextMonth = () => {
    const nextMonth = selectedDate.add(1, 'month');
    onYearChange(nextMonth.year());
    onMonthChange(monthNames[nextMonth.month()]);
  };

  // Quick period shortcuts
  const handleQuickPeriod = (monthsAgo: number) => {
    const targetDate = today.subtract(monthsAgo, 'month');
    onYearChange(targetDate.year());
    onMonthChange(monthNames[targetDate.month()]);
  };

  const isFutureMonth = selectedDate.isAfter(today, 'month');

  return (
    <Box>
      {/* Date picker with navigation arrows and quick select in one row on desktop */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: 2, 
        mb: 2,
        flexWrap: isTablet ? 'wrap' : 'nowrap',
      }}>
        {/* Date Picker Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Previous month">
            <IconButton 
              onClick={navigateToPreviousMonth}
              size="small"
              disabled={isLoading}
              sx={{ 
                color: themeColor.main,
                border: `1px solid ${themeColor.main}`,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            >
              <ChevronLeft />
            </IconButton>
          </Tooltip>
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              views={['year', 'month']}
              openTo="month"
              disabled={isLoading}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: false,
                  sx: { 
                    minWidth: isMobile ? 160 : 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontWeight: 600,
                      color: themeColor.main,
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: themeColor.main,
                        }
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: themeColor.main,
                        }
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontWeight: 600,
                    }
                  }
                },
                actionBar: {
                  actions: ['today', 'accept'],
                },
              }}
            />
          </LocalizationProvider>

          <Tooltip title="Next month">
            <IconButton 
              onClick={navigateToNextMonth}
              size="small"
              disabled={isLoading}
              sx={{ 
                color: themeColor.main,
                border: `1px solid ${themeColor.main}`,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Quick Select Chips */}
        {!isTablet && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label="This Month"
              size="small"
              onClick={() => handleQuickPeriod(0)}
              variant={isCurrentMonth ? "filled" : "outlined"}
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: isCurrentMonth ? 'white' : themeColor.main,
                backgroundColor: isCurrentMonth ? themeColor.main : 'transparent',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: isCurrentMonth ? themeColor.dark : `${themeColor.main}15`,
                }
              }}
            />
            <Chip
              label="Last Month"
              size="small"
              onClick={() => handleQuickPeriod(1)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: themeColor.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            />
            <Chip
              label="2 Months Ago"
              size="small"
              onClick={() => handleQuickPeriod(2)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: themeColor.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            />
            <Chip
              label="3 Months Ago"
              size="small"
              onClick={() => handleQuickPeriod(3)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: themeColor.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            />
          </Stack>
        )}
      </Box>

      {/* Quick Select Chips on separate row for tablets/mobile */}
      {isTablet && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label="This Month"
              size="small"
              onClick={() => handleQuickPeriod(0)}
              variant={isCurrentMonth ? "filled" : "outlined"}
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: isCurrentMonth ? 'white' : themeColor.main,
                backgroundColor: isCurrentMonth ? themeColor.main : 'transparent',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: isCurrentMonth ? themeColor.dark : `${themeColor.main}15`,
                }
              }}
            />
            <Chip
              label="Last Month"
              size="small"
              onClick={() => handleQuickPeriod(1)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: themeColor.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            />
            <Chip
              label="2 Months Ago"
              size="small"
              onClick={() => handleQuickPeriod(2)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: themeColor.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            />
            <Chip
              label="3 Months Ago"
              size="small"
              onClick={() => handleQuickPeriod(3)}
              variant="outlined"
              disabled={isLoading}
              sx={{
                borderColor: themeColor.main,
                color: themeColor.main,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: `${themeColor.main}15`,
                }
              }}
            />
          </Stack>
        </Box>
      )}

      {/* Period context display - Compact inline version */}
      {!isLoading && (transactionCount !== undefined || isCurrentMonth) && (
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1,
            p: 1.25,
            px: 1.25,
            borderRadius: '12px',
            backgroundColor: `${themeColor.main}08`,
            border: `1px solid ${themeColor.main}20`,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 1, py: 0.8, borderRadius: '10px', backgroundColor: `${themeColor.main}12` }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              Period
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: themeColor.main }}>
              {daysInMonth} days
            </Typography>
          </Box>
          
          {transactionCount !== undefined && (
            <Box sx={{ px: 1, py: 0.8, borderRadius: '10px', backgroundColor: `${themeColor.main}12` }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                Transactions
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: themeColor.main }}>
                {transactionCount}
              </Typography>
            </Box>
          )}
          
          {isCurrentMonth && (
            <Box sx={{ px: 1, py: 0.8, borderRadius: '10px', backgroundColor: `${themeColor.main}12` }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                Days Remaining
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: themeColor.main }}>
                {daysRemaining}
              </Typography>
            </Box>
          )}

          <Box sx={{ px: 1, py: 0.8, borderRadius: '10px', backgroundColor: `${themeColor.main}12` }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              Status
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: themeColor.main }}>
              {isCurrentMonth ? 'In Progress' : isFutureMonth ? 'Future' : 'Complete'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MonthYearPicker; 